/**
 * State closing taxes for buyer estimates: the deed / transfer tax on the
 * sale price and the borrower's mortgage / recordation taxes on the loan.
 *
 * Replaces calculateTransferTaxFactor, which charged the buyer 100% of an
 * often-wrong transfer tax (Texas 1.5%, Tennessee 3.75%, Utah, Oregon and
 * Wyoming ~1% — none of which have a transfer tax at that rate) and fell back
 * to a factor of 1 — a "tax" equal to the whole price — for unknown ZIPs.
 * It also missed taxes the borrower really pays on the loan (Florida doc
 * stamps + intangible tax, Georgia intangible tax, New York mortgage
 * recording tax, Minnesota registry tax, etc.).
 *
 * Rates are statewide / typical-county figures from state revenue
 * department schedules and title-industry rate summaries. Cities and
 * counties can add local taxes (noted per state). Entries with
 * `confirm: true` vary enough by county that they should be checked with a
 * title company — see IMPROVEMENT_PLAN.md. Who pays the deed tax follows
 * TRANSFER_TAX_CUSTOM (the same table the contract uses) unless the parties
 * agreed otherwise.
 */
import {
  normalizeStateName,
  TRANSFER_TAX_CUSTOM,
  type TransferTaxPayer,
} from '@/utils/contractState';

type Tier = { upTo?: number; rate: number };

export type StateTaxRule = {
  key: string;
  label: string;
  /** What the rate applies to. */
  base: 'price' | 'loan';
  /** Flat rate (fraction). */
  rate?: number;
  /**
   * marginal: each tier's rate applies to the portion inside it.
   * bracket: the whole base is taxed at the rate of the tier it falls in.
   */
  tiers?: Tier[];
  tierMode?: 'marginal' | 'bracket';
  /** Applies only when the base is at least this much (e.g. mansion taxes). */
  minBase?: number;
  /** 'custom' → TRANSFER_TAX_CUSTOM for the state. */
  payer: TransferTaxPayer | 'custom';
  /** Purchase only (deed taxes), or also on refinance (loan taxes). */
  transactions: Array<'purchase' | 'refinance'>;
  description: string;
  confirm?: boolean;
};

export type StateTaxLine = {
  key: string;
  label: string;
  description: string;
  /** Full tax. */
  totalAmount: number;
  /** The buyer/borrower's share. */
  buyerAmount: number;
  payer: TransferTaxPayer;
  confirm: boolean;
};

export type StateClosingTaxes = {
  state: string;
  /** Buyer's share of deed / transfer taxes. */
  transferTaxes: number;
  /** Borrower's mortgage / recordation / intangible taxes on the loan. */
  mortgageTaxes: number;
  lines: StateTaxLine[];
  /** Plain-English local-tax caveat for the state, if any. */
  localNote?: string;
};

/** Deed/transfer tax on the price; pass NaN as `rate` when `tiers` apply. */
const deed = (
  rate: number,
  description: string,
  extra: Partial<StateTaxRule> = {}
): StateTaxRule => ({
  key: 'transferTax',
  label: 'Transfer tax',
  base: 'price',
  rate: Number.isFinite(rate) ? rate : undefined,
  payer: 'custom',
  transactions: ['purchase'],
  description,
  ...extra,
});

const loanTax = (
  key: string,
  label: string,
  rate: number,
  description: string,
  extra: Partial<StateTaxRule> = {}
): StateTaxRule => ({
  key,
  label,
  base: 'loan',
  rate,
  payer: 'buyer',
  transactions: ['purchase', 'refinance'],
  description,
  ...extra,
});

export const STATE_CLOSING_TAXES: Record<
  string,
  { rules: StateTaxRule[]; localNote?: string }
> = {
  Alabama: {
    rules: [
      deed(0.001, 'Deed tax of $1.00 per $1,000 of the price.'),
      loanTax(
        'mortgageTax',
        'Mortgage recording tax',
        0.0015,
        'Mortgage tax of $0.15 per $100 of the loan.'
      ),
    ],
  },
  Alaska: { rules: [] },
  Arizona: { rules: [] },
  Arkansas: {
    rules: [
      deed(0.0033, 'Real property transfer tax of $3.30 per $1,000.', {
        confirm: true,
      }),
    ],
  },
  California: {
    rules: [
      deed(0.0011, 'County documentary transfer tax of $1.10 per $1,000.'),
    ],
    localNote:
      'Some California cities (San Francisco, Los Angeles, Oakland, Berkeley and others) add their own transfer tax.',
  },
  Colorado: {
    rules: [
      deed(0.0001, 'State documentary fee of $0.01 per $100.', {
        confirm: true,
      }),
    ],
    localNote:
      'Some Colorado towns (e.g. mountain resort towns) add a local transfer tax.',
  },
  Connecticut: {
    rules: [
      deed(
        NaN,
        'State conveyance tax (0.75% up to $800,000, 1.25% on the next $1.7M, 2.25% above $2.5M) plus 0.25% municipal tax.',
        {
          tiers: [
            { upTo: 800_000, rate: 0.01 },
            { upTo: 2_500_000, rate: 0.015 },
            { rate: 0.025 },
          ],
          tierMode: 'marginal',
        }
      ),
    ],
  },
  Delaware: {
    rules: [deed(0.04, 'Realty transfer tax of 4% (state 2.5% + local 1.5%).')],
  },
  'District of Columbia': {
    rules: [
      {
        key: 'transferTax',
        label: 'Deed transfer tax',
        base: 'price',
        tiers: [{ upTo: 399_999.99, rate: 0.011 }, { rate: 0.0145 }],
        tierMode: 'bracket',
        payer: 'seller',
        transactions: ['purchase'],
        description:
          'DC deed transfer tax (1.1% under $400,000, 1.45% at or above).',
      },
      {
        key: 'recordationTax',
        label: 'Recordation tax',
        base: 'price',
        tiers: [{ upTo: 399_999.99, rate: 0.011 }, { rate: 0.0145 }],
        tierMode: 'bracket',
        payer: 'buyer',
        transactions: ['purchase'],
        description:
          'DC deed recordation tax (1.1% under $400,000, 1.45% at or above), customarily paid by the buyer.',
      },
    ],
  },
  Florida: {
    rules: [
      deed(
        0.007,
        'Documentary stamp tax on the deed of $0.70 per $100 (Miami-Dade $0.60).'
      ),
      loanTax(
        'mortgageDocStamps',
        'Mortgage doc stamps',
        0.0035,
        'Documentary stamp tax on the note of $0.35 per $100 of the loan.'
      ),
      loanTax(
        'intangibleTax',
        'Intangible tax',
        0.002,
        'Non-recurring intangible tax of 0.2% of the loan.'
      ),
    ],
  },
  Georgia: {
    rules: [
      deed(0.001, 'State transfer tax of $1.00 per $1,000.'),
      loanTax(
        'intangibleTax',
        'Intangible recording tax',
        0.003,
        'Intangible recording tax of $1.50 per $500 of the loan.'
      ),
    ],
  },
  Hawaii: {
    rules: [
      deed(NaN, 'Conveyance tax, graduated by price (owner-occupant rates).', {
        tiers: [
          { upTo: 599_999.99, rate: 0.001 },
          { upTo: 999_999.99, rate: 0.002 },
          { upTo: 1_999_999.99, rate: 0.003 },
          { upTo: 3_999_999.99, rate: 0.005 },
          { upTo: 5_999_999.99, rate: 0.007 },
          { upTo: 9_999_999.99, rate: 0.009 },
          { rate: 0.01 },
        ],
        tierMode: 'bracket',
        confirm: true,
      }),
    ],
  },
  Idaho: { rules: [] },
  Illinois: {
    rules: [
      deed(0.0015, 'State ($1.00) and county ($0.50) transfer tax per $1,000.'),
    ],
    localNote:
      'Chicago and some other Illinois cities add a municipal transfer tax, part of it paid by the buyer.',
  },
  Indiana: { rules: [] },
  Iowa: {
    rules: [deed(0.0016, 'Real estate transfer tax of $0.80 per $500.')],
  },
  Kansas: { rules: [] },
  Kentucky: {
    rules: [deed(0.001, 'Real estate transfer tax of $0.50 per $500.')],
  },
  Louisiana: {
    rules: [],
    localNote: 'A few Louisiana parishes charge a small transfer fee.',
  },
  Maine: {
    rules: [
      deed(
        0.0044,
        'Real estate transfer tax of $2.20 per $500, split between buyer and seller.'
      ),
    ],
  },
  Maryland: {
    rules: [
      deed(
        0.015,
        'State transfer tax (0.5%) plus county transfer and recordation taxes; roughly 1.5% combined, customarily split.',
        {
          confirm: true,
        }
      ),
    ],
    localNote:
      'Maryland county transfer and recordation rates vary widely; first-time buyers get a partial state exemption.',
  },
  Massachusetts: {
    rules: [deed(0.00456, 'Deeds excise tax of $4.56 per $1,000.')],
  },
  Michigan: {
    rules: [
      deed(0.0086, 'State ($7.50) and county ($1.10) transfer tax per $1,000.'),
    ],
  },
  Minnesota: {
    rules: [
      deed(0.0033, 'Deed tax of 0.33% of the price.'),
      loanTax(
        'mortgageRegistryTax',
        'Mortgage registry tax',
        0.0024,
        'Mortgage registry tax of 0.24% of the loan.'
      ),
    ],
  },
  Mississippi: { rules: [] },
  Missouri: { rules: [] },
  Montana: { rules: [] },
  Nebraska: {
    rules: [deed(0.00225, 'Documentary stamp tax of $2.25 per $1,000.')],
  },
  Nevada: {
    rules: [
      deed(
        0.0051,
        'Real property transfer tax (Clark County $5.10 per $1,000; most other counties $3.90).',
        { confirm: true }
      ),
    ],
  },
  'New Hampshire': {
    rules: [
      deed(
        0.015,
        'Real estate transfer tax of $0.75 per $100 each from buyer and seller.'
      ),
    ],
  },
  'New Jersey': {
    rules: [
      deed(
        0.01,
        'Realty transfer fee (graduated, about 1% on typical prices), paid by the seller.',
        { confirm: true }
      ),
      {
        key: 'mansionTax',
        label: 'Mansion tax',
        base: 'price',
        rate: 0.01,
        minBase: 1_000_000,
        payer: 'buyer',
        transactions: ['purchase'],
        description:
          '1% tax paid by the buyer on residential purchases of $1,000,000 or more.',
      },
    ],
  },
  'New Mexico': { rules: [] },
  'New York': {
    rules: [
      deed(0.004, 'State transfer tax of $2 per $500, paid by the seller.'),
      {
        key: 'mansionTax',
        label: 'Mansion tax',
        base: 'price',
        rate: 0.01,
        minBase: 1_000_000,
        payer: 'buyer',
        transactions: ['purchase'],
        description:
          '1% tax paid by the buyer on residential purchases of $1,000,000 or more.',
      },
      loanTax(
        'mortgageRecordingTax',
        'Mortgage recording tax',
        0.0105,
        'Mortgage recording tax, about 1.05% of the loan outside New York City (varies by county).',
        {
          confirm: true,
        }
      ),
    ],
    localNote:
      'New York City adds its own transfer tax and a higher mortgage recording tax (1.8%–1.925%).',
  },
  'North Carolina': { rules: [deed(0.002, 'Excise tax of $1 per $500.')] },
  'North Dakota': { rules: [] },
  Ohio: {
    rules: [
      deed(
        0.001,
        'Conveyance fee of $1 per $1,000 (counties may charge up to $4).',
        { confirm: true }
      ),
    ],
  },
  Oklahoma: {
    rules: [
      deed(0.0015, 'Documentary stamp tax of $0.75 per $500.'),
      loanTax(
        'mortgageTax',
        'Mortgage tax',
        0.001,
        'Mortgage tax of $0.10 per $100 of the loan.'
      ),
    ],
  },
  Oregon: {
    rules: [],
    localNote: 'Washington County charges a $1 per $1,000 transfer tax.',
  },
  Pennsylvania: {
    rules: [
      deed(
        0.02,
        'Realty transfer tax of 1% state plus about 1% local, customarily split.'
      ),
    ],
    localNote:
      'Philadelphia and Pittsburgh local transfer taxes are higher than 1%.',
  },
  'Rhode Island': {
    rules: [deed(0.0046, 'Real estate conveyance tax of $2.30 per $500.')],
  },
  'South Carolina': {
    rules: [
      deed(0.0037, 'Deed recording fee of $1.85 per $500 (state and county).'),
    ],
  },
  'South Dakota': { rules: [deed(0.001, 'Transfer fee of $0.50 per $500.')] },
  Tennessee: {
    rules: [
      deed(
        0.0037,
        'Recordation tax of $0.37 per $100, customarily paid by the buyer.'
      ),
      loanTax(
        'mortgageTax',
        'Mortgage tax',
        0.000115,
        'Indebtedness tax of $0.115 per $100 of the loan.'
      ),
    ],
  },
  Texas: { rules: [] },
  Utah: { rules: [] },
  Vermont: {
    rules: [
      deed(
        NaN,
        'Property transfer tax (principal residence: 0.5% on the first $100,000, 1.45% above, including the clean water surcharge).',
        {
          tiers: [{ upTo: 100_000, rate: 0.005 }, { rate: 0.0145 }],
          tierMode: 'marginal',
          confirm: true,
        }
      ),
    ],
  },
  Virginia: {
    rules: [
      {
        key: 'transferTax',
        label: 'Grantor tax',
        base: 'price',
        rate: 0.001,
        payer: 'seller',
        transactions: ['purchase'],
        description: 'Grantor tax of $0.50 per $500, paid by the seller.',
      },
      {
        key: 'recordationTax',
        label: 'Recordation tax',
        base: 'price',
        rate: 0.00333,
        payer: 'buyer',
        transactions: ['purchase'],
        description:
          'State ($0.25) and local ($0.083) recordation tax per $100, paid by the buyer.',
        confirm: true,
      },
    ],
  },
  Washington: {
    rules: [
      deed(
        NaN,
        'Graduated real estate excise tax (1.1% up to $525,000, 1.28% to $1.525M, 2.75% to $3.025M, 3% above), paid by the seller.',
        {
          tiers: [
            { upTo: 525_000, rate: 0.011 },
            { upTo: 1_525_000, rate: 0.0128 },
            { upTo: 3_025_000, rate: 0.0275 },
            { rate: 0.03 },
          ],
          tierMode: 'marginal',
        }
      ),
    ],
    localNote:
      'Washington cities and counties add 0.25%–0.5% local excise tax.',
  },
  'West Virginia': {
    rules: [
      deed(0.0044, 'State and county excise tax of $2.20 per $500.', {
        confirm: true,
      }),
    ],
  },
  Wisconsin: {
    rules: [deed(0.003, 'Real estate transfer fee of $3 per $1,000.')],
  },
  Wyoming: { rules: [] },
};

/** ZIP3 prefix ranges → state (USPS). */
const ZIP3_RANGES: Array<[number, number, string]> = [
  [5, 5, 'New York'],
  [10, 27, 'Massachusetts'],
  [28, 29, 'Rhode Island'],
  [30, 38, 'New Hampshire'],
  [39, 49, 'Maine'],
  [50, 54, 'Vermont'],
  [55, 55, 'Massachusetts'],
  [56, 59, 'Vermont'],
  [60, 69, 'Connecticut'],
  [70, 89, 'New Jersey'],
  [100, 149, 'New York'],
  [150, 196, 'Pennsylvania'],
  [197, 199, 'Delaware'],
  [200, 200, 'District of Columbia'],
  [201, 201, 'Virginia'],
  [202, 205, 'District of Columbia'],
  [206, 219, 'Maryland'],
  [220, 246, 'Virginia'],
  [247, 268, 'West Virginia'],
  [270, 289, 'North Carolina'],
  [290, 299, 'South Carolina'],
  [300, 319, 'Georgia'],
  [320, 349, 'Florida'],
  [350, 369, 'Alabama'],
  [370, 385, 'Tennessee'],
  [386, 397, 'Mississippi'],
  [398, 399, 'Georgia'],
  [400, 427, 'Kentucky'],
  [430, 459, 'Ohio'],
  [460, 479, 'Indiana'],
  [480, 499, 'Michigan'],
  [500, 528, 'Iowa'],
  [530, 549, 'Wisconsin'],
  [550, 567, 'Minnesota'],
  [569, 569, 'District of Columbia'],
  [570, 577, 'South Dakota'],
  [580, 588, 'North Dakota'],
  [590, 599, 'Montana'],
  [600, 629, 'Illinois'],
  [630, 658, 'Missouri'],
  [660, 679, 'Kansas'],
  [680, 693, 'Nebraska'],
  [700, 714, 'Louisiana'],
  [716, 729, 'Arkansas'],
  [730, 732, 'Oklahoma'],
  [733, 733, 'Texas'],
  [734, 749, 'Oklahoma'],
  [750, 799, 'Texas'],
  [800, 816, 'Colorado'],
  [820, 831, 'Wyoming'],
  [832, 838, 'Idaho'],
  [840, 847, 'Utah'],
  [850, 865, 'Arizona'],
  [870, 884, 'New Mexico'],
  [885, 885, 'Texas'],
  [889, 898, 'Nevada'],
  [900, 961, 'California'],
  [967, 968, 'Hawaii'],
  [970, 979, 'Oregon'],
  [980, 994, 'Washington'],
  [995, 999, 'Alaska'],
];

export function stateFromZip(zip?: number | string | null): string {
  const digits = String(zip ?? '').replace(/\D/g, '');
  if (!digits) return '';
  const z3 = Math.floor(Number(digits.padStart(5, '0').slice(0, 5)) / 100);
  const hit = ZIP3_RANGES.find(([lo, hi]) => z3 >= lo && z3 <= hi);
  return hit ? hit[2] : '';
}

function taxOn(rule: StateTaxRule, base: number): number {
  if (!(base > 0)) return 0;
  if (rule.minBase && base < rule.minBase) return 0;
  if (rule.tiers?.length) {
    if (rule.tierMode === 'bracket') {
      const t = rule.tiers.find((x) => x.upTo == null || base <= x.upTo);
      return base * (t?.rate ?? 0);
    }
    let tax = 0;
    let floor = 0;
    for (const t of rule.tiers) {
      const top = t.upTo ?? Infinity;
      if (base > floor) tax += (Math.min(base, top) - floor) * t.rate;
      floor = top;
    }
    return tax;
  }
  return base * (rule.rate ?? 0);
}

const BUYER_SHARE: Record<TransferTaxPayer, number> = {
  buyer: 1,
  split: 0.5,
  seller: 0,
  none: 0,
};

/**
 * The closing taxes a buyer/borrower should expect for a property.
 * `transferTaxPayer` overrides the state custom (e.g. from the contract's
 * transfer-tax election). Unknown states return zero rather than guessing.
 */
export function estimateStateClosingTaxes(opts: {
  state?: string | null;
  zipCode?: number | string | null;
  price?: number;
  loanAmount?: number;
  transaction?: 'purchase' | 'refinance';
  transferTaxPayer?: TransferTaxPayer | null;
}): StateClosingTaxes {
  const state =
    normalizeStateName(opts.state) || stateFromZip(opts.zipCode) || '';
  const entry = STATE_CLOSING_TAXES[state];
  const transaction = opts.transaction || 'purchase';
  const price = Math.max(0, Number(opts.price) || 0);
  const loanAmount = Math.max(0, Number(opts.loanAmount) || 0);
  const out: StateClosingTaxes = {
    state,
    transferTaxes: 0,
    mortgageTaxes: 0,
    lines: [],
    localNote: entry?.localNote,
  };
  if (!entry) return out;

  for (const rule of entry.rules) {
    if (!rule.transactions.includes(transaction)) continue;
    const total = taxOn(rule, rule.base === 'loan' ? loanAmount : price);
    if (!(total > 0)) continue;
    const payer: TransferTaxPayer =
      rule.payer === 'custom'
        ? opts.transferTaxPayer || TRANSFER_TAX_CUSTOM[state] || 'seller'
        : rule.payer;
    const buyerAmount = Math.round(total * BUYER_SHARE[payer] * 100) / 100;
    out.lines.push({
      key: rule.key,
      label: rule.label,
      description: rule.description,
      totalAmount: Math.round(total * 100) / 100,
      buyerAmount,
      payer,
      confirm: !!rule.confirm,
    });
    if (rule.base === 'loan') out.mortgageTaxes += buyerAmount;
    else out.transferTaxes += buyerAmount;
  }
  out.transferTaxes = Math.round(out.transferTaxes * 100) / 100;
  out.mortgageTaxes = Math.round(out.mortgageTaxes * 100) / 100;
  return out;
}
